import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import NotFoundPage from '@/components/ui/not-found';
import { Spinner } from '@/components/ui/spinner';
import { ROUTES } from '@/config/routes';
import { useNavigationService } from '@/services/navigate-service';
import { useIntegrationStatus } from '../hooks/useIntegrationStatus';
import { useSessionPmsAccount } from '../hooks/useSessionPmsAccount';
import type { PmsAccountRef } from '../services/pmsAccount';
import { useSyncProgress } from '../hooks/useSyncProgress';
import { getVendorDefinition } from './vendorRegistry';
import { hasVendorAccess } from './vendorAccess';
import { LockedVendorCard } from './LockedVendorCard';
import { useVendorSettings } from './useVendorSettings';
import { useIntegrationStatusActions } from './useIntegrationStatusActions';
import { VendorSettingsLayout } from './VendorSettingsLayout';
import { IntegrationStatusLabel } from './status/IntegrationStatusLabel';
import { DisabledIntegrationBanner } from './status/DisabledIntegrationBanner';
import { SyncProgressBanner } from './status/SyncProgressBanner';
import { DangerZoneCard } from './status/DangerZoneCard';
import { DisableIntegrationDialog } from './status/DisableIntegrationDialog';
import type { VendorDefinition } from './types';

const VendorSettings = ({ vendor, account }: { vendor: VendorDefinition; account: PmsAccountRef }) => {
  // `canManage` gates the disabled banner's re-enable button and the Danger
  // Zone card's visibility.
  const { status, isLoading: isStatusLoading, canManage } = useIntegrationStatus(account);
  const [searchParams] = useSearchParams();
  const { navigateTo } = useNavigationService();
  // Decide access only once the status has resolved: during load the hook
  // reads `none`, which admits every vendor for a frame (spec §4.7).
  const hasAccess = !isStatusLoading && hasVendorAccess(vendor.key, status, searchParams);
  const state = useVendorSettings(vendor, { integrationStatus: status, account });
  const actions = useIntegrationStatusActions({
    vendorName: vendor.displayName,
    pmsSource: vendor.key,
    origin: 'settings',
    account,
  });
  // Watch sync progress whenever the user may see this page — not gated on
  // `status.kind`, which reads 'none' until a first-connection save's sync
  // mutation settles (minutes later): gating on it would hide the banner for
  // exactly the save it exists to show progress for.
  const { sync, runningOrigin, completedOrigin } = useSyncProgress({ origin: 'settings', account, skip: !hasAccess });
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isStatusLoading) {
    return (
      <section className="flex flex-col min-h-full">
        <div className="flex items-center justify-center py-20">
          <Spinner size="lg" />
        </div>
      </section>
    );
  }
  // The `kind !== 'none'` narrowing is only for TypeScript — `hasVendorAccess`
  // already admits every vendor while nothing is connected. `status.pmsSource`
  // is an unchecked cast over wire data (toIntegrationStatus), so it is
  // resolved through the guarded lookup and falls back to the raw wire value
  // rather than indexing the registry blind.
  if (!hasAccess && status.kind !== 'none') {
    return (
      <LockedVendorCard
        vendorName={vendor.displayName}
        connectedVendorName={getVendorDefinition(status.pmsSource)?.displayName ?? status.pmsSource}
        onBack={() => navigateTo(ROUTES.INTEGRATIONS)}
      />
    );
  }
  const banner = status.kind === 'disabled' ? (
    <DisabledIntegrationBanner
      vendorName={vendor.displayName}
      status={status}
      canManage={canManage}
      onReenable={actions.reenable}
      isReenabling={actions.isReenabling}
      error={actions.reenableError}
    />
  ) : null;
  const dangerZone = status.kind === 'active' && canManage ? (
    <DangerZoneCard vendorName={vendor.displayName} onDisable={() => setConfirmOpen(true)} />
  ) : null;
  const confirmDisable = async () => {
    const ok = await actions.disable();
    if (ok) setConfirmOpen(false);
  };
  // A disabled integration has no in-flight or just-finished sync worth
  // reporting — the disabled banner above already owns that slot.
  const banners = (
    <>
      {banner}
      {status.kind !== 'disabled' && (
        <SyncProgressBanner
          sync={sync}
          startedHere={runningOrigin === 'settings'}
          justCompleted={completedOrigin === 'settings'}
        />
      )}
    </>
  );
  return (
    <>
      <VendorSettingsLayout
        vendor={vendor}
        state={state}
        statusSlot={<IntegrationStatusLabel status={status} connectionState={state.ctx.connectionState} />}
        beforeTabs={banners}
        afterSettings={dangerZone}
      />
      <DisableIntegrationDialog
        open={confirmOpen}
        vendorName={vendor.displayName}
        isConfirming={actions.isDisabling}
        onConfirm={confirmDisable}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

/** Route target for /home/integrations/vendor/:id — one page for every vendor. */
const VendorSettingsPage = () => {
  const { id } = useParams();
  const account = useSessionPmsAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  // A Buildium `?token=` return belongs to the account it arrived in. The
  // panel strips it with history.replaceState, which the router never sees, so
  // after an in-place account switch (the header tree's 'stay') the router
  // still holds it, and the remount below would save it again, now for the
  // new account. Drop it through the router before remounting.
  const arrivedIn = useRef(account);
  const staleOAuthReturn = account !== arrivedIn.current && searchParams.has('token');
  useEffect(() => {
    if (staleOAuthReturn) setSearchParams({}, { replace: true });
  }, [staleOAuthReturn, setSearchParams]);
  const vendor = getVendorDefinition(id);
  if (!vendor) return <NotFoundPage />;
  // /home is entity-required, so there is always an account; nothing to name otherwise.
  if (!account || staleOAuthReturn) return null;
  // Keyed on vendor AND account so either change remounts the hook and panel
  // state (record, settings baseline, mode, mount-only panel reads) instead of
  // leaking the previous one's into the next.
  return <VendorSettings key={`${vendor.key}:${account}`} vendor={vendor} account={account} />;
};

export default VendorSettingsPage;
